import { Link } from "react-router-dom";
import { Shield, Mail, Phone } from "lucide-react";

/**
 * Política de Privacidad pública.
 * URL: https://app.rotulaciondnf.com/privacidad
 *
 * Requerida por Meta para publicar la WhatsApp Business API. También
 * cumple con el deber informativo del RGPD (art. 13) y la LOPDGDD.
 */
export default function Privacidad() {
  const ULT_ACT = "10 de junio de 2026";

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/40 backdrop-blur sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80">
            <Shield className="w-5 h-5 text-amber-300" />
            <span className="font-semibold tracking-wide">DNF Rotulación</span>
          </Link>
          <span className="text-xs text-zinc-500">Última actualización: {ULT_ACT}</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 space-y-10">
        <header>
          <h1 className="text-3xl font-bold text-zinc-100">Política de Privacidad</h1>
          <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
            En DNF Rotulación nos comprometemos a proteger la información personal de nuestros clientes,
            proveedores, empleados y colaboradores. Este documento explica qué datos recogemos, para qué
            los usamos y los derechos que te asisten en virtud del Reglamento General de Protección de
            Datos (UE) 2016/679 (RGPD) y la Ley Orgánica 3/2018, de Protección de Datos Personales y
            Garantía de los Derechos Digitales (LOPDGDD).
          </p>
        </header>

        <section className="space-y-2" id="responsable">
          <h2 className="text-xl font-semibold text-amber-300">1. Responsable del tratamiento</h2>
          <ul className="text-sm text-zinc-300 space-y-1 pl-4">
            <li><b>Denominación:</b> DNF Rotulación, S.L.</li>
            <li><b>CIF:</b> B-75693010</li>
            <li><b>Domicilio:</b> Avda. Ramón y Cajal, 7 — 41220 Burguillos (Sevilla), España</li>
            <li><b>Correo electrónico:</b> info@rotulaciondnf.com</li>
            <li><b>Web:</b> https://www.rotulaciondnf.com</li>
            <li><b>App:</b> https://app.rotulaciondnf.com</li>
          </ul>
        </section>

        <section className="space-y-2" id="datos">
          <h2 className="text-xl font-semibold text-amber-300">2. Datos que recogemos</h2>
          <p className="text-sm text-zinc-300 leading-relaxed">
            Tratamos exclusivamente los datos necesarios para la prestación de nuestros servicios:
          </p>
          <ul className="text-sm text-zinc-300 list-disc pl-6 space-y-1">
            <li><b>Identificativos</b>: nombre, NIF/CIF, dirección postal.</li>
            <li><b>Contacto</b>: teléfono, número de WhatsApp, correo electrónico.</li>
            <li><b>Comerciales</b>: histórico de pedidos, presupuestos, facturas y pagos.</li>
            <li><b>Operativos</b>: dirección de los espacios donde se realizan instalaciones, fotos de
              obra antes y después del montaje (solo de los elementos instalados, no de personas).</li>
            <li><b>Mensajería WhatsApp Business</b>: contenido y metadatos de las conversaciones que
              mantienes con nosotros, número de teléfono, perfil público.</li>
            <li><b>Técnicos</b> (solo navegación): IP, navegador, fecha y hora de acceso, logs de seguridad.</li>
          </ul>
        </section>

        <section className="space-y-2" id="finalidades">
          <h2 className="text-xl font-semibold text-amber-300">3. Finalidades y base legal</h2>
          <table className="w-full text-xs border border-zinc-800 mt-2">
            <thead className="bg-zinc-900/60">
              <tr><th className="p-2 text-left">Finalidad</th><th className="p-2 text-left">Base legal (RGPD art. 6)</th></tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              <tr><td className="p-2">Gestión comercial, presupuestos y facturación</td><td className="p-2">Ejecución de contrato (6.1.b)</td></tr>
              <tr><td className="p-2">Cumplimiento fiscal y contable</td><td className="p-2">Obligación legal (6.1.c)</td></tr>
              <tr><td className="p-2">Envío de notificaciones operativas por WhatsApp/email (estado del montaje, factura emitida, etc.)</td><td className="p-2">Ejecución de contrato (6.1.b)</td></tr>
              <tr><td className="p-2">Envío de comunicaciones comerciales</td><td className="p-2">Consentimiento (6.1.a) — revocable</td></tr>
              <tr><td className="p-2">Atención al cliente vía WhatsApp</td><td className="p-2">Ejecución de contrato (6.1.b)</td></tr>
              <tr><td className="p-2">Recordatorio de cobros pendientes</td><td className="p-2">Interés legítimo (6.1.f)</td></tr>
              <tr><td className="p-2">Mejora del servicio (analítica, logs de errores)</td><td className="p-2">Interés legítimo (6.1.f)</td></tr>
            </tbody>
          </table>
        </section>

        <section className="space-y-2" id="conservacion">
          <h2 className="text-xl font-semibold text-amber-300">4. Plazos de conservación</h2>
          <ul className="text-sm text-zinc-300 list-disc pl-6 space-y-1">
            <li><b>Datos fiscales y facturación</b>: 6 años (Código de Comercio art. 30).</li>
            <li><b>Datos del cliente para la relación contractual</b>: mientras dure la relación + 4 años.</li>
            <li><b>Conversaciones de WhatsApp</b>: 2 años desde el último mensaje, salvo obligación legal de conservación superior.</li>
            <li><b>Logs técnicos</b>: 12 meses.</li>
            <li><b>Datos para envío de comunicaciones comerciales</b>: hasta revocación del consentimiento.</li>
          </ul>
        </section>

        <section className="space-y-2" id="destinatarios">
          <h2 className="text-xl font-semibold text-amber-300">5. Destinatarios y transferencias internacionales</h2>
          <p className="text-sm text-zinc-300 leading-relaxed">
            Tus datos no se ceden a terceros salvo a:
          </p>
          <ul className="text-sm text-zinc-300 list-disc pl-6 space-y-1">
            <li><b>Administraciones públicas</b> cuando exista obligación legal (Hacienda, Seguridad Social).</li>
            <li><b>Proveedores que actúan como encargados del tratamiento</b>, todos ellos vinculados por contrato (RGPD art. 28):
              <ul className="list-circle pl-6 mt-1 space-y-0.5 text-zinc-400">
                <li>Meta Platforms Ireland Ltd. (WhatsApp Business API) — Irlanda</li>
                <li>Resend Inc. (envío de email transaccional) — EE.UU. (con cláusulas tipo)</li>
                <li>FacturaDirecta SL (facturación) — España</li>
                <li>MongoDB Atlas / proveedores cloud — UE</li>
                <li>OpenRouteService — Alemania (rutas)</li>
                <li>Wialon (telemetría vehículos) — UE</li>
                <li>Google Cloud (Gemini API para análisis OCR de tickets) — UE</li>
              </ul>
            </li>
          </ul>
          <p className="text-xs text-zinc-400 mt-2">
            Las transferencias internacionales fuera del EEE se realizan con base en Cláusulas Contractuales Tipo
            aprobadas por la Comisión Europea o decisiones de adecuación.
          </p>
        </section>

        <section className="space-y-2" id="derechos">
          <h2 className="text-xl font-semibold text-amber-300">6. Tus derechos</h2>
          <p className="text-sm text-zinc-300 leading-relaxed">
            Puedes ejercer en cualquier momento los siguientes derechos:
          </p>
          <ul className="text-sm text-zinc-300 list-disc pl-6 space-y-1">
            <li><b>Acceso</b> a tus datos personales.</li>
            <li><b>Rectificación</b> de datos inexactos o incompletos.</li>
            <li><b>Supresión</b> ("derecho al olvido") cuando ya no sean necesarios.</li>
            <li><b>Oposición</b> y <b>limitación</b> del tratamiento.</li>
            <li><b>Portabilidad</b> de tus datos a otro responsable.</li>
            <li><b>Revocación del consentimiento</b> en cualquier momento (sin efecto retroactivo).</li>
          </ul>
          <p className="text-sm text-zinc-300 leading-relaxed mt-2">
            Para ejercerlos escríbenos a <a className="text-amber-300 hover:underline" href="mailto:info@rotulaciondnf.com">info@rotulaciondnf.com</a> indicando tu nombre, el derecho que ejerces y una copia de tu DNI/NIE para verificar tu identidad. Te responderemos en un plazo máximo de un mes.
          </p>
          <p className="text-xs text-zinc-400 mt-2">
            Si consideras que el tratamiento no es conforme a la normativa, puedes presentar una reclamación ante la
            Agencia Española de Protección de Datos: <a className="text-amber-300 hover:underline" href="https://www.aepd.es" target="_blank" rel="noreferrer">www.aepd.es</a>.
          </p>
        </section>

        <section className="space-y-2" id="whatsapp">
          <h2 className="text-xl font-semibold text-amber-300">7. Uso de WhatsApp Business</h2>
          <p className="text-sm text-zinc-300 leading-relaxed">
            Usamos la API oficial de WhatsApp Business proporcionada por Meta para:
          </p>
          <ul className="text-sm text-zinc-300 list-disc pl-6 space-y-1">
            <li>Atender consultas que tú nos envíes voluntariamente desde WhatsApp.</li>
            <li>Enviarte notificaciones operativas relacionadas con tu pedido (montaje finalizado, factura emitida, cita agendada, recordatorio de pago).</li>
          </ul>
          <p className="text-sm text-zinc-300 leading-relaxed">
            Las plantillas que utilizamos están previamente aprobadas por Meta. Puedes detener en
            cualquier momento estas comunicaciones respondiendo <b>BAJA</b> a cualquiera de nuestros
            mensajes, o escribiendo a info@rotulaciondnf.com.
          </p>
        </section>

        <section className="space-y-2" id="seguridad">
          <h2 className="text-xl font-semibold text-amber-300">8. Medidas de seguridad</h2>
          <p className="text-sm text-zinc-300 leading-relaxed">
            Aplicamos las medidas técnicas y organizativas apropiadas para garantizar un nivel de
            seguridad adecuado al riesgo: cifrado en tránsito (TLS 1.3), control de acceso por roles,
            registro de auditoría, copias de seguridad cifradas, y revisiones periódicas.
          </p>
        </section>

        <section className="space-y-2" id="cambios">
          <h2 className="text-xl font-semibold text-amber-300">9. Cambios en esta política</h2>
          <p className="text-sm text-zinc-300 leading-relaxed">
            Esta política puede actualizarse periódicamente. Publicaremos siempre la versión vigente en
            esta misma URL. Los cambios sustanciales se comunicarán por email o WhatsApp a los usuarios
            registrados.
          </p>
        </section>

        <section className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-4 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between" id="contacto">
          <div>
            <h3 className="text-base font-semibold text-zinc-100">Contacto</h3>
            <p className="text-xs text-zinc-400">Para cualquier consulta sobre privacidad o ejercicio de derechos:</p>
          </div>
          <div className="flex flex-col gap-1 text-sm">
            <a href="mailto:info@rotulaciondnf.com" className="flex items-center gap-2 text-amber-300 hover:underline">
              <Mail className="w-4 h-4" /> info@rotulaciondnf.com
            </a>
            <a href="tel:+34854802024" className="flex items-center gap-2 text-zinc-300 hover:text-amber-300">
              <Phone className="w-4 h-4" /> +34 854 80 20 24
            </a>
          </div>
        </section>

        <footer className="text-xs text-zinc-500 pt-6 border-t border-zinc-800">
          © {new Date().getFullYear()} DNF Rotulación, S.L. — Todos los derechos reservados.
          <Link to="/" className="ml-3 text-amber-300 hover:underline">Volver a la app</Link>
        </footer>
      </main>
    </div>
  );
}
